import React from 'react';
import { Button, Spin, Tooltip, message } from 'antd';
import { JobOutput } from 'types';
import styled from 'styled-components';
import {
  DownloadOutlined,
  LoadingOutlined,
  CheckOutlined,
  CaretRightOutlined,
  ClockCircleOutlined,
  FileDoneOutlined,
  TagOutlined,
  IdcardOutlined,
  FileSearchOutlined,
  RedoOutlined,
  CloseOutlined,
  FilePdfOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import Icon, { DocumentIcon } from 'hyarchis-global/components/icon';
import moment from 'moment';
import { DATE_TIME_FORMAT } from 'app-constants';
import JobStatus from './JobStatus';
import downloadFile from './downloadFile';
import downloadFileAsText from './downloadFileAsText';
import Styled from '../listItemHead.styled';
import { JobCrossMatch } from '../../../types/proccesingJobs';
import ColorPickerService from '../../../services/helpers/colorPickerService';

const {
  LeftColumn,
  Wrapper,
  HeadIconWrapper,
  HeadingWrapper,
  HeadTitleWrapper,
  ResultIconsWrapper,
  DateTimeWrapper,
  CollapseTogglerWrapper,
  DownloadButton,
  SmallIconSpan,
  JobTitle,
  JobValue,
  HeadTitle,
  DateTimeItem,
  StyledStatusIcon,
  StyledOutputTitle,
  SubTitleWrapper,
  FlexColumn,
  ResultTitleWrapper,
  FlexBox,
  ActionIconsWrapper,
} = Styled;

const StyledJobStatus = styled(JobStatus)`
  display: flex;
  align-items: center;
`;

interface OwnProps {
  jobFile: JobOutput;
  collapsed: boolean;
  crossMatches: JobCrossMatch[];
  onCollapseClick: () => void;
  onRemove: (processingJobId: string, documentId: string | undefined) => void;
  onRefresh: ((processingJobId: string, documentId: string | undefined) => void) | undefined;
}

const JobListItemHead = ({
  jobFile,
  collapsed,
  crossMatches,
  onCollapseClick,
  onRemove,
  onRefresh,
}: OwnProps) => {
  const { error } = jobFile;
  const job = jobFile.job || undefined;

  const isLoading = !jobFile.skipProcessing && (!job || (job.status === 'inProgress' && !error));
  const jobSucceeded = !!job && job.status === 'succeeded';
  const jobDone = !!job && job.status !== 'inProgress';
  const isError = !!job && (!!error || job.status !== 'succeeded');

  const crossMatchColor = ColorPickerService.getBorderColor(crossMatches);

  const documentTypeProcessed =
    jobSucceeded && !!job.output.documentType && !!job.output.documentType.documentType;
  const documentTypeLabel = documentTypeProcessed
    ? `DocumentType: ${job.output.documentType?.documentType}`
    : 'Document type classification not executed';

  const idExistanceProcessed = jobSucceeded && !!job.output.idExistanceInPages;
  const idExistanceFound = idExistanceProcessed && job.output.idExistanceInPages!.length > 0;
  let idExistanceLabel = 'ID/Passport classification not executed';
  if (idExistanceFound) {
    idExistanceLabel = `ID/Passport at pages: ${job.output.idExistanceInPages!.join(', ')}`;
  } else if (idExistanceProcessed) {
    idExistanceLabel = 'No ID/Passport found';
  }

  const dataExtractionProcessed =
    jobSucceeded && (!!job.output.documentDataPoints || !!job.output.perPage);
  const dataExtractionFound =
    dataExtractionProcessed &&
    ((job.output.documentDataPoints && job.output.documentDataPoints.length > 0) ||
      (job.output.perPage && job.output.perPage.length > 0));
  let dataExtractionLabel = 'Data extraction not executed';
  if (dataExtractionFound) {
    dataExtractionLabel = 'Data extracted';
  } else if (dataExtractionProcessed) {
    dataExtractionLabel = 'Extraction executed, but 0 data points not found.';
  }

  const copyJobId = () => {
    if (job) {
      navigator.clipboard.writeText(job?.processingJobId);
      message.success('Job Id copied to clipboard.', 1);
    }
  };

  const copyDocumentId = () => {
    if (jobFile.documentId) {
      navigator.clipboard.writeText(jobFile.documentId);
      message.success('Document Id copied to clipboard.', 1);
    }
  };

  const getOutputIconType = () => {
    if (!job) return 'document';

    if (job.output.pdfFileId && job.output.textFileId) {
      return 'document';
    }

    if (job.output.pdfFileId) {
      return 'pdf';
    }

    if (job.output.textFileId) {
      return 'txt';
    }

    return 'document';
  };

  return (
    <Wrapper color={crossMatchColor}>
      <LeftColumn>
        <HeadIconWrapper>
          {isLoading ? (
            <Spin indicator={<LoadingOutlined style={{ fontSize: 35 }} spin />} />
          ) : (
            <FileDoneOutlined />
          )}
        </HeadIconWrapper>
        <HeadingWrapper>
          {jobFile.riskAssessmentResults && (
            <HeadTitleWrapper>
              <Tooltip title={jobFile.riskAssessmentResults.personName}>
                <HeadTitle>{jobFile.riskAssessmentResults.personName}</HeadTitle>
              </Tooltip>
            </HeadTitleWrapper>
          )}
          {jobFile.file && (
            <HeadTitleWrapper>
              <DocumentIcon sizeType='large' type={jobFile.file.extension} size={20} />
              <Tooltip title={jobFile.file.name}>
                <HeadTitle>{jobFile.file.name}</HeadTitle>
              </Tooltip>
              {jobFile.job && (
                <Tooltip title='Download original file'>
                  <DownloadButton
                    onClick={downloadFile(
                      jobFile.job.initialFileId,
                      `${jobFile.file?.name}.${jobFile.file?.extension}`,
                    )}
                    size='small'
                    shape='circle'
                    icon={<DownloadOutlined />}
                  />
                </Tooltip>
              )}
            </HeadTitleWrapper>
          )}
          {!jobFile.skipProcessing && (
            <SubTitleWrapper>
              <JobTitle>JOB ID:</JobTitle>
              <Tooltip title='Click to copy'>
                <JobValue onClick={copyJobId}>
                  {jobFile.job ? jobFile.job.processingJobId : 'uploading...'}
                </JobValue>
              </Tooltip>
            </SubTitleWrapper>
          )}
          {jobFile.documentId && (
            <SubTitleWrapper>
              <JobTitle>DOCUMENT ID:</JobTitle>
              <Tooltip title='Click to copy'>
                <JobValue onClick={copyDocumentId}>{jobFile.documentId}</JobValue>
              </Tooltip>
            </SubTitleWrapper>
          )}
        </HeadingWrapper>
      </LeftColumn>
      {jobSucceeded && jobFile.file && (
        <FlexColumn>
          <ResultTitleWrapper>
            <DocumentIcon sizeType='large' type={getOutputIconType()} size={20} />
            <StyledOutputTitle disabled={!job.output.pdfFileId && !job.output.textFileId}>
              Output
            </StyledOutputTitle>
            {job.output.pdfFileId && (
              <Tooltip title='Download as PDF'>
                <DownloadButton
                  onClick={downloadFile(
                    job.output.pdfFileId,
                    `${job.createdAt}--${job.processingJobId}.pdf`,
                  )}
                  size='small'
                  shape='circle'
                  icon={<FilePdfOutlined />}
                />
              </Tooltip>
            )}
            {job.output.textFileId && (
              <Tooltip title='Download as Text'>
                <DownloadButton
                  onClick={downloadFileAsText(
                    job.output.textFileId,
                    `${job.createdAt}--${job.processingJobId}.txt`,
                  )}
                  size='small'
                  shape='circle'
                  icon={<FileTextOutlined />}
                />
              </Tooltip>
            )}
          </ResultTitleWrapper>
          <ResultIconsWrapper>
            <StyledStatusIcon
              icon={TagOutlined}
              title={documentTypeLabel}
              enabled={documentTypeProcessed}
            />
            <StyledStatusIcon
              icon={IdcardOutlined}
              title={idExistanceLabel}
              enabled={idExistanceFound}
            />
            <StyledStatusIcon
              icon={FileSearchOutlined}
              title={dataExtractionLabel}
              enabled={dataExtractionFound}
            />
          </ResultIconsWrapper>
        </FlexColumn>
      )}
      {isError && (
        <StyledJobStatus
          status={job.status}
          message={error || job.error?.errorMessage || ''}
          timeout={!!error}
        />
      )}
      <FlexBox>
        {job && (
          <DateTimeWrapper>
            <DateTimeItem>
              <SmallIconSpan>
                <CaretRightOutlined />
              </SmallIconSpan>
              <span>{moment(job.createdAt).format(DATE_TIME_FORMAT)}</span>
            </DateTimeItem>
            <DateTimeItem>
              <SmallIconSpan>
                <CheckOutlined />
              </SmallIconSpan>
              <span>{jobDone ? moment(job.finishedAt).format(DATE_TIME_FORMAT) : '— —'}</span>
            </DateTimeItem>
            <DateTimeItem>
              <SmallIconSpan>
                <ClockCircleOutlined />
              </SmallIconSpan>
              <span>
                {jobDone
                  ? moment(moment(job.finishedAt).diff(moment(job.createdAt))).format('mm:ss')
                  : '— —'}
              </span>
            </DateTimeItem>
          </DateTimeWrapper>
        )}
        <CollapseTogglerWrapper>
          <Button
            icon={<Icon icon={!collapsed ? 'chevronUp' : 'chevronDown'} type='regular' size={24} />}
            size='large'
            type='text'
            onClick={(e) => onCollapseClick()}
            disabled={!job || !jobSucceeded}
          />
        </CollapseTogglerWrapper>
      </FlexBox>
      {(job || jobFile.skipProcessing) && (
        <ActionIconsWrapper>
          {onRefresh && (
            <Button
              size='small'
              type='text'
              onClick={(e) => onRefresh(job?.processingJobId ?? '', jobFile.documentId)}
            >
              <RedoOutlined />
            </Button>
          )}
          <Button
            size='small'
            type='text'
            onClick={(e) => onRemove(job?.processingJobId ?? '', jobFile.documentId)}
          >
            <CloseOutlined />
          </Button>
        </ActionIconsWrapper>
      )}
    </Wrapper>
  );
};

export default React.memo(JobListItemHead);
